import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeActionPriceData} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppeBusinessEntityDtoIsNotFound,
    DoppeExternalMembershipPlanProvider,
    doppeExternalMembershipPlanProviderResolveBusinessEntityReference
} from '../../client-server-common';
import type {RichTextContent, Values, HideableValue } from '@wix/devzai-utils-common';
import {
    arrayRemoveNullValues,
    asArray,
    immerProduce,
    stringTrimPrefix,
    urlGetHostname,
    urlNormalizeHttpUrl, hideableValueCreateHidden, hideableValueGetValue, hideableValueModifyValue } from '@wix/devzai-utils-common';
import {DoppeActionTypeMembershipViewerLang} from './doppe-action-type-membership-viewer.lang';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {DoppeDtoMembershipPlan} from '../../client-server-common/types/doppe-dto-membership-plan';
import {
    doppeDtoMembershipPlanPaymentConvertToActionFormattedPrice
} from '../../client-server-common/types/doppe-dto-membership-plan';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    doppeActionCtaButtonTypeGetDefaultSettings,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonCreate
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';


export const DoppeActionTypeMembershipType = {
    Other: 'Other',
    Patreon: 'Patreon',
    Deviantart: 'Deviantart',
    Youtube: 'Youtube',
    Twitter: 'Twitter',
    Twitch: 'Twitch',
    Memberful: 'Memberful',
} as const;

export type DoppeActionTypeMembershipType = Values<typeof DoppeActionTypeMembershipType>;


export namespace DoppeActionTypeMembership {

    export interface PlanSettings {
        membershipPageLink: string;
        membershipImages: WixMediaResource[] | null;
        membershipTitle: HideableValue<string>;
        membershipDescription: HideableValue<RichTextContent>;
        priceData: HideableValue<DoppeActionPriceData>;
        membershipBenefits: string[];
        membershipType: DoppeActionTypeMembershipType;
        couponCode: HideableValue<string>;
        ribbon: HideableValue<string>;
        membershipProvider: DoppeExternalMembershipPlanProvider | null;
        membershipProviderId: string | null;
    }

    export type ActionSettings = DoppeActionTypeMembership.PlanSettings &
        DoppeActionContentSectionsSettings &
        DoppeActionCtaButtonsSettings & {

    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        linkButtonText?: HideableValue<string>;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;



}

export const doppeActionTypeMembership = doppeActionTypeDefineMetadata<DoppeActionTypeMembership.ActionSettings, DoppeActionTypeMembership.PartialProps>({
    id: 'io.bymo.action/membership',
    resolveActionSettings: action => {

        const {
            membershipPageLink = '',
            linkButtonText = '',
            ctaButton,
            ctaButtons,
            sections,
            priceData,
            ...restSettings
        } = action.settings;

        return {
            membershipPageLink: membershipPageLink,
            priceData: hideableValueModifyValue(priceData ?? {actualPrice: DoppeActionTypeMembershipViewerLang.defaultTexts.price, discountPrice: ''}, value => ({...value, discountPrice: value.discountPrice ?? ''})),
            membershipBenefits: [DoppeActionTypeMembershipViewerLang.defaultTexts.membershipBenefit],
            membershipType: DoppeActionTypeMembershipType.Other,
            linkButtonText: linkButtonText,
            couponCode: hideableValueCreateHidden(''),
            membershipProvider: null,
            membershipProviderId: null,
            membershipImages: null,
            membershipTitle: hideableValueCreateHidden(''),
            membershipDescription: hideableValueCreateHidden(''),
            ribbon: hideableValueCreateHidden(''),
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: linkButtonText,
                showButton: true,
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
                ctaButtons: ctaButtons,
            }, true),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            membershipPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
            membershipImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            membershipTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            membershipDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            priceData: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.PriceData, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            membershipBenefits: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.ListOfTexts, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                membershipDescription: true,
                membershipTitle: true,
                priceData: true,
                ribbon: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.membershipProvider !== null && actionSettings.membershipProviderId !== null ?
                doppeExternalMembershipPlanProviderResolveBusinessEntityReference(
                    actionSettings.membershipProvider,
                    actionSettings.membershipProviderId
                ) :
                null
        ])
    },
    resolveActionEntityBinding: actionSettings => {
        const {membershipProvider, membershipProviderId} = actionSettings;
        return membershipProvider !== null && membershipProviderId !== null
            ? doppeExternalMembershipPlanProviderResolveBusinessEntityReference(membershipProvider, membershipProviderId)
            : null;
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.membershipPageLink ?? null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveUsedMedia: actionSettings => {
        return actionSettings.membershipImages ?? [];
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.membershipImages?.[0] ?? null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.membershipImages) {
                actionSettings.membershipImages = actionSettings.membershipImages.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.membershipPageLink = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {membershipProvider, membershipProviderId} = actionSettings;
        if (membershipProvider !== null && membershipProviderId !== null) {
            const boundEntity = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalMembershipPlanProviderResolveBusinessEntityReference(membershipProvider, membershipProviderId)
            );
            if (boundEntity && !doppeBusinessEntityDtoIsNotFound(boundEntity)) {
                return {
                    ...actionSettings,
                    ...doppeActionTypeMembershipPlanResolveSettingsFromBoundEntity(actionSettings, boundEntity as DoppeDtoMembershipPlan),
                };
            }
        }
        return actionSettings;
    },
});


export function doppeActionTypeMembershipResolveTypeFromUrl(url: string) {

    const hostname = stringTrimPrefix(urlGetHostname(urlNormalizeHttpUrl(url)), 'www.')

    switch (hostname) {
        case 'patreon.com':
            return DoppeActionTypeMembershipType.Patreon;
        case 'twitch.tv':
            return DoppeActionTypeMembershipType.Twitch;
        case 'twitter.com':
            return DoppeActionTypeMembershipType.Twitter;
        case 'youtube.com':
            return DoppeActionTypeMembershipType.Youtube;
        case 'deviantart.com':
            return DoppeActionTypeMembershipType.Deviantart;
        default:
            return DoppeActionTypeMembershipType.Other;
    }

}

export function doppeActionTypeMembershipPlanResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeMembership.PlanSettings,
    _doppeDtoMembershipPlan: DoppeDtoMembershipPlan,
): DoppeActionTypeMembership.PlanSettings {

    // const location = arrayFirst(doppeDtoService.locations);

    return {
        ...actionSettings,
        // membershipPageLink: stringIsNotNullableOrWhiteSpace(_doppeDtoMembershipPlan.membershipPageUrl) ?
        //     doppeDtoService.membershipPageUrl : 'about:blank',
    }
}

export function populateMembershipPlanActionSettingsDefaultTexts(options: {
    plan: DoppeActionTypeMembership.ActionSettings,
    pageContentLocales: DoppeAppPageContentLocales,
}): DoppeActionTypeMembership.ActionSettings {

    const {
        plan,
        pageContentLocales,
    } = options;

    return {
        ...plan,
        membershipTitle: hideableValueGetValue(plan.membershipTitle) !== '' ? plan.membershipTitle : pageContentLocales['membership.actionSettings.title'],
        membershipDescription: hideableValueModifyValue(plan.membershipDescription, (value) => {
            return value !== '' ? value : pageContentLocales['membership.actionSettings.description'];
        }),
        couponCode: hideableValueModifyValue(plan.couponCode, (value) => {
            return value !== '' ? value : pageContentLocales['membership.actionSettings.couponCode'];
        }),
        ribbon: hideableValueModifyValue(plan.ribbon, (value) => {
            return value !== '' ? value : pageContentLocales['membership.actionSettings.ribbon'];
        }),
        sections: [],
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: [doppeActionMultipleCTAButtonCreate({
                text: pageContentLocales["membership.actionSettings.buttonText"],
                isRequired: false,
                settings: doppeActionCtaButtonTypeGetDefaultSettings(DoppeActionCTAType.Link, true)
            })],
        }, false),
    }
}


export function convertDoppeWixMembershipPlanToPlanSettings(plan: DoppeDtoMembershipPlan, websiteUrl?: string, settings?: DoppeActionTypeMembership.ActionSettings, membershipProvider?: DoppeExternalMembershipPlanProvider): DoppeActionTypeMembership.ActionSettings {

    return {
        ctaButtons: [],
        sections: [],
        ribbon: hideableValueCreateHidden(''),
        couponCode: hideableValueCreateHidden(''),
        ...settings,
        membershipImages: plan.media ? asArray(plan.media) : null,
        membershipPageLink: websiteUrl ?? settings?.membershipPageLink ?? ``,
        priceData: plan.pricing ? {actualPrice: doppeDtoMembershipPlanPaymentConvertToActionFormattedPrice(plan.pricing, ''), discountPrice: ''} : settings?.priceData ?? hideableValueCreateHidden({actualPrice: '', discountPrice: ''}),
        membershipTitle: plan?.name ?? '',
        membershipDescription: plan.description ?? hideableValueCreateHidden(''),
        membershipProvider: membershipProvider ?? DoppeExternalMembershipPlanProvider.Wix,
        membershipProviderId: plan.id,
        membershipType: DoppeActionTypeMembershipType.Other,
        membershipBenefits: plan.perks ?? settings?.membershipBenefits ?? [],
    }
}

export function doppeActionTypeMembershipResolveDefaultSettingByType(membershipType: DoppeActionTypeMembershipType, pageContentLocales: DoppeAppPageContentLocales) {
    switch (membershipType) {
        case DoppeActionTypeMembershipType.Deviantart:
            return {
                title: pageContentLocales['membership.defaultContent.deviantart.title'],
                membershipBenefits: pageContentLocales['membership.defaultContent.deviantart.benefits'],
                membershipPageLink: 'https://www.deviantart.com/username'
            }
        case DoppeActionTypeMembershipType.Patreon:
            return {
                title: pageContentLocales['membership.defaultContent.patreon.title'],
                membershipBenefits: pageContentLocales['membership.defaultContent.patreon.benefits'],
                membershipPageLink: 'https://www.patreon.com/username'
            }
        case DoppeActionTypeMembershipType.Youtube:
            return {
                title: pageContentLocales['membership.defaultContent.youtube.title'],
                membershipBenefits: pageContentLocales['membership.defaultContent.youtube.benefits'],
                membershipPageLink: 'https://www.youtube.com/username'
            }
        case DoppeActionTypeMembershipType.Twitter:
            return {
                title: pageContentLocales['membership.defaultContent.x.title'],
                membershipBenefits: pageContentLocales['membership.defaultContent.x.benefits'],
                membershipPageLink: 'https://www.x.com/username',
                linkButtonText: pageContentLocales['membership.defaultContent.x.linkButtonText']
            }
        case DoppeActionTypeMembershipType.Twitch:
            return {
                title: pageContentLocales['membership.defaultContent.twitch.title'],
                membershipBenefits: pageContentLocales['membership.defaultContent.twitch.benefits'],
                membershipPageLink: 'https://www.twitch.tv/username',
                linkButtonText: pageContentLocales['membership.defaultContent.twitch.linkButtonText']
            }
        case DoppeActionTypeMembershipType.Memberful:
            return {
                title: pageContentLocales['membership.defaultContent.memberful.title'],
                membershipBenefits: pageContentLocales['membership.defaultContent.memberful.benefits'],
                membershipPageLink: 'https://www.your-memberful-page-url.com',
                linkButtonText: pageContentLocales['membership.defaultContent.memberful.linkButtonText'],
            }
        case DoppeActionTypeMembershipType.Other:
            return {
                title: pageContentLocales['membership.defaultContent.other.title'],
                membershipBenefits: pageContentLocales['membership.defaultContent.other.benefits'],
                membershipPageLink: 'https://www.your-memebership-page-url.com'
            }
    }
}

export function doppeActionTypeMembershipResolveDefaultSettingByTypeWithTexts(membershipType: DoppeActionTypeMembershipType) {
    switch (membershipType) {
        case DoppeActionTypeMembershipType.Deviantart:
            return {
                title: 'Support me on DeviantArt',
                membershipBenefits: ['Monthly Sketch Dumps & W.I.P. posts', 'Plush & Product Previews', 'Support my work 🙏'],
                membershipPageLink: 'https://www.deviantart.com/username'
            }
        case DoppeActionTypeMembershipType.Patreon:
            return {
                title: 'Support me on Patreon',
                membershipBenefits: ['Ad-free content', 'Exclusive Content', 'Behind-the-scenes content', 'Live chat'],
                membershipPageLink: 'https://www.patreon.com/username'
            }
        case DoppeActionTypeMembershipType.Youtube:
            return {
                title: 'Support me on Youtube',
                membershipBenefits: ['Ad-free content', 'Exclusive Content', 'Behind-the-scenes content', 'Live chat'],
                membershipPageLink: 'https://www.youtube.com/username'
            }
        case DoppeActionTypeMembershipType.Twitter:
            return {
                title: 'Super Follow me on Twitter',
                membershipBenefits: ['Ad-free content', 'Exclusive Content', 'Behind-the-scenes content', 'Live chat'],
                membershipPageLink: 'https://www.twitter.com/username',
                linkButtonText: 'Super Follow'
            }
        case DoppeActionTypeMembershipType.Twitch:
            return {
                title: 'Support me on Twitch',
                membershipBenefits: ['Ad-free content', 'Channel Points 1.2x', 'Sub-Only Chat', 'Subscriber Streams'],
                membershipPageLink: 'https://www.twitch.tv/username',
                linkButtonText: 'Subscribe'
            }
        case DoppeActionTypeMembershipType.Memberful:
            return {
                title: 'My Membership Benefits',
                membershipBenefits: ['Ad-free content', 'Exclusive Content', 'Behind-the-scenes content', 'Live chat'],
                membershipPageLink: 'https://www.your-memberful-page-url.com',
                linkButtonText: 'Become a Member'
            }
        case DoppeActionTypeMembershipType.Other:
            return {
                title: 'Support my Membership Page',
                membershipBenefits: ['Ad-free content', 'Exclusive Content', 'Behind-the-scenes content', 'Live chat'],
                membershipPageLink: 'https://www.your-memebership-page-url.com'
            }
    }

}